import { StepsResponse } from '~/modals/CalcSimplifiedCoverage/hooks/use-get-plans';
import { Product } from '~/store/slices/quote/types';

/**
 * ID do produto Aride Park
 */
export const ARIDE_PARK_PRODUCT_ID = 8;

/**
 * ID do produto Seguro Residencial — exibido como R$ 0,00 no card do cliente,
 * mas mantido no total cobrado.
 */
export const RESIDENTIAL_INSURANCE_PRODUCT_ID = 7;

/**
 * ID do produto de Rastreamento — quando presente na cotação, exige o aceite
 * do termo de uso padrão (General) antes de permitir a contratação.
 */
export const TRACKING_PRODUCT_ID = 10;

/**
 * Retorna o preço a ser exibido no card do produto para o cliente.
 * Para o Seguro Residencial (ID 7), exibe R$ 0,00 independentemente do
 * product_price retornado pela API.
 */
export const getDisplayProductPrice = (productId: string | number, productPrice: number): number => {
  const id = typeof productId === 'string' ? parseInt(productId, 10) : productId;
  if (id === RESIDENTIAL_INSURANCE_PRODUCT_ID) return 0;
  return productPrice;
};

/**
 * Verifica se uma promoção não está disponível com base nos dados do fluxo
 * @param data - Dados do fluxo retornados pela API
 * @returns true se a promoção existir mas não estiver disponível
 */
export const isPromotionUnavailable = (data?: StepsResponse): boolean => {
  return data?.has_promotion === true && data?.is_promotion_available === false;
};

/**
 * Verifica se o modal de cotação simplificada deve ser exibido
 * @param data - Dados do fluxo retornados pela API
 * @returns true se o modal deve ser exibido
 */
export const shouldShowQuoteModal = (data?: StepsResponse): boolean => {
  return !isPromotionUnavailable(data);
};

/**
 * Verifica se é o fluxo do Aride Park
 * O fluxo Aride Park é ativado quando:
 * - Apenas o produto com ID 8 (Aride Park) está selecionado/required
 * - Nenhum outro produto está checked=true ou required=true
 *
 * @param products - Lista de produtos selecionados (já filtrados)
 * @returns true se for o fluxo Aride Park
 */
export const isArideParkFlow = (products: Product[]): boolean => {
  if (!products || products.length !== 1) {
    return false;
  }

  const onlyProduct = products[0];
  return (
    onlyProduct.product_id === ARIDE_PARK_PRODUCT_ID.toString() ||
    parseInt(onlyProduct.product_id) === ARIDE_PARK_PRODUCT_ID
  );
};

/**
 * Verifica se é o fluxo do Aride Park a partir da resposta da API
 * O fluxo Aride Park é ativado quando:
 * - O produto com ID 8 tem checked=true OU required=true
 * - Nenhum outro produto tem checked=true OU required=true
 *
 * @param quotes - Lista de produtos retornados pela API
 * @returns true se for o fluxo Aride Park
 */
export const isArideParkFlowFromAPI = (
  quotes: Array<{
    product_id: number;
    checked: boolean;
    required: boolean;
  }>
): boolean => {
  if (!quotes || quotes.length === 0) {
    return false;
  }

  // Filtrar produtos que estão ativos (checked OU required)
  const activeProducts = quotes.filter((q) => q.checked === true || q.required === true);

  // Deve ter exatamente 1 produto ativo
  if (activeProducts.length !== 1) {
    return false;
  }

  // O único produto ativo deve ser o Aride Park (ID 8)
  const onlyActiveProduct = activeProducts[0];
  return onlyActiveProduct.product_id === ARIDE_PARK_PRODUCT_ID;
};
